import React from 'react';
import { PieChart, Pie, Tooltip } from 'recharts';

interface DonutChartProps {
  /** Slice values. Here they are already percentages of the total (≈100). */
  series: number[];
  labels: string[];
  colors: string[];
  title?: string | null;
  /** Kept for backwards-compat; the donut renders at the fixed Figma 176px. */
  height?: number | string;
}

// Figma geometry (Content Analysis donut): 176×176 canvas, outer r≈85.6,
// inner r≈52.8, ~2.3px rounded segment caps, ~6px gap between segments.
const RADIAN = Math.PI / 180;
const SIZE = 176;
const INNER_RADIUS = 53;
const OUTER_RADIUS = 86;
const CORNER_RADIUS = 2;
const PADDING_ANGLE = 4;

interface DonutDatum {
  name: string;
  value: number;
  /** Recharts colours each sector from the datum's `fill`. */
  fill: string;
}

// Recharts types the label render props loosely (every field optional, and
// cx/cy can be number|string). Mirror that and coerce so the function is
// assignable to Recharts' PieLabel without leaning on internal exported types.
interface DonutLabelProps {
  cx?: number | string;
  cy?: number | string;
  midAngle?: number;
  innerRadius?: number | string;
  outerRadius?: number | string;
  value?: number | string;
}

const num = (v: number | string | undefined): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

// % label placed on the mid-radius of each segment, matching the Figma where
// the labels sit within the ring band (10px / 500 / #383838 / 0.32px tracking).
const renderLabel = (props: DonutLabelProps) => {
  const cx = num(props.cx);
  const cy = num(props.cy);
  const midAngle = num(props.midAngle);
  const r = (num(props.innerRadius) + num(props.outerRadius)) / 2;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#383838"
      fontFamily="Manrope"
      fontSize={10}
      fontWeight={500}
      letterSpacing="0.32px"
      textAnchor="middle"
      dominantBaseline="central"
    >
      {`${Math.round(num(props.value))}%`}
    </text>
  );
};

interface DonutTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; payload: DonutDatum }>;
}

// White 8px-radius card with a bold content-type title, a colored dot, and a
// "{n}% Submission" label — same spec as the engagement-bar hover tooltip.
const DonutTooltip = ({ active, payload }: DonutTooltipProps) => {
  if (!active || !payload || payload.length === 0) return null;
  const item = payload[0];
  const datum = item.payload;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '8px 18px',
        background: '#FFFFFF',
        borderRadius: 8,
        boxShadow: '0px 2px 6px 2px #00000026',
        fontFamily: 'Manrope, sans-serif',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          fontWeight: 700,
          fontSize: 12,
          lineHeight: '16px',
          color: '#020D3A',
        }}
      >
        {datum.name}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            width: 16,
            height: 16,
            borderRadius: 10,
            background: datum.fill,
            display: 'inline-block',
            flexShrink: 0,
          }}
        />
        <span
          style={{
            fontWeight: 400,
            fontSize: 12,
            lineHeight: '16px',
            color: '#020D3A',
          }}
        >
          {`${Math.round(item.value)}% Submission`}
        </span>
      </span>
    </div>
  );
};

const DonutChart: React.FC<DonutChartProps> = ({
  series,
  labels,
  colors,
  title,
}) => {
  // The legend lists every content form (Post/Reel/Story) regardless of value.
  const data: DonutDatum[] = series.map((value, i) => ({
    name: labels[i] ?? '',
    value,
    fill: colors[i] ?? '#000000',
  }));

  // The ring itself drops zero-value slices: Recharts still reserves a
  // `paddingAngle` gap and renders a `0%` label for them, which cuts a wedge
  // out of an otherwise-full ring (e.g. a 100% single category) plus a stray
  // "0%" label.
  const pieData = data.filter((d) => d.value > 0);

  return (
    <div
      className="h-full flex flex-col"
      style={{ position: 'relative', gap: 16 }}
    >
      <div className="text-[#9B919D] text-xs">{title}</div>
      {/* Chart Container — donut + legend, inset 40px L/R and centered, with
          8px top/bottom padding (Figma "Chart Container": padding 8px 40px). */}
      <div
        className="flex-grow flex items-center justify-center"
        style={{ gap: 40, padding: '8px 40px' }}
      >
        <PieChart width={SIZE} height={SIZE}>
          <Pie
            data={pieData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={INNER_RADIUS}
            outerRadius={OUTER_RADIUS}
            cornerRadius={CORNER_RADIUS}
            paddingAngle={PADDING_ANGLE}
            // Start at the top (12 o'clock) and sweep clockwise so the first
            // slice mirrors the Figma orientation.
            startAngle={90}
            endAngle={-270}
            stroke="none"
            labelLine={false}
            label={renderLabel}
            isAnimationActive={false}
          />
          <Tooltip
            content={<DonutTooltip />}
            wrapperStyle={{ zIndex: 20, outline: 'none' }}
          />
        </PieChart>

        {/* Legend — 16×16 dots + 12px #9B919D labels, 16px row gap (Figma). */}
        <div className="flex flex-col" style={{ gap: 16 }}>
          {data.map((d) => (
            <div key={d.name} className="flex items-center" style={{ gap: 16 }}>
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 10,
                  background: d.fill,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontFamily: 'Manrope',
                  fontWeight: 400,
                  fontSize: 12,
                  lineHeight: '100%',
                  color: '#9B919D',
                }}
              >
                {d.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DonutChart;
